// Suchleiste im Header mit Vorschlägen direkt unter dem Feld (kein Modal). Nutzt Shopifys Predictive-Search-API
// (/search/suggest.json, mit Sprachpräfix) und zeigt Produkte, Kollektionen und Rezepte; Enter oder „Rechercher …“
// führt zur Ergebnisseite. Pfeiltasten wählen Treffer, Escape schließt. Texte kommen als data-label-* aus
// snippets/search.liquid (Sprachdateien), Preise im Geldformat des Shops.

const MIN = 2;
const DEBOUNCE = 180;
let corePromise;

class FkSearch extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[type="search"]');
    this.panel = this.querySelector('.fk-search__panel');
    this.clear = this.querySelector('.fk-search__clear');
    this.format = this.dataset.moneyFormat || '{{amount}}';
    this.active = -1;
    this.controller = null;
    this.timer = null;
    if (!this.input || !this.panel) return;

    this.input.addEventListener('input', () => {
      this.close();
      this.clear.hidden = !this.input.value;
      clearTimeout(this.timer);
      const q = this.input.value.trim();
      if (q.length < MIN) { this.controller?.abort(); return this.close(); }
      this.timer = setTimeout(() => this.search(q), DEBOUNCE);
    });
    this.input.addEventListener('focus', () => {
      if (this.panel.childElementCount && this.renderedQuery === this.input.value.trim() && this.input.value.trim().length >= MIN) this.open();
    });
    this.input.addEventListener('keydown', (e) => this.onKey(e));
    this.clear.addEventListener('click', () => {
      clearTimeout(this.timer);
      this.controller?.abort();
      this.input.value = '';
      this.clear.hidden = true;
      this.close();
      this.input.focus();
    });
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) this.close();
    });
    this.form.addEventListener('submit', (e) => {
      if (this.input.value.trim().length === 0) e.preventDefault();
    });
  }

  async search(q) {
    this.controller?.abort();
    this.controller = new AbortController();
    const controller = this.controller;
    const params = new URLSearchParams({
      q,
      'resources[type]': 'product,collection,article',
      'resources[limit]': '6',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,product_type,vendor,variants.title,variants.sku,variants.barcode',
    });
    this.setAttribute('aria-busy', 'true');
    try {
      const root = window.Shopify?.routes?.root || '/';
      const catalogue = this.dataset.coreUrl ? (corePromise ||= import(this.dataset.coreUrl))
        .then(async (core) => ({ core, products: await core.loadCatalogue({ root, context: this.dataset.context }) })) : null;
      // Handle the shared catalogue rejection immediately, including while native suggestions are still pending.
      const indexed = catalogue?.catch(() => null);
      const res = await fetch(`${root}search/suggest.json?${params}`, { signal: controller.signal, headers: { Accept: 'application/json' } })
        .catch((error) => { if (error.name === 'AbortError') throw error; return null; });
      const data = res?.ok ? await res.json().catch(() => null) : null;
      if (controller.signal.aborted || this.input.value.trim() !== q) return;
      const results = data?.resources?.results || {};
      if (data) this.renderResults(q, results);
      else this.panel.innerHTML = `<div class="fk-search__section"><a class="fk-search__all" data-result href="${this.form.action}?type=product&amp;q=${encodeURIComponent(q)}">${this.label('searchFor', 'Search for “[query]”', q)}</a></div>`;
      this.open();
      const enhanced = await indexed;
      if (!enhanced || controller.signal.aborted || this.input.value.trim() !== q) return;
      const match = enhanced.core.searchProducts(enhanced.products, q, { partial: true });
      if (!match.products.length && !match.query.reference && results.products?.length) {
        this.renderResults(q, results, 'shopify-fallback');
        this.open();
        return;
      }
      results.products = match.products.slice(0, 6).map((product) => {
        const variant = product.matchedVariant;
        const price = variant?.price ?? product.price;
        const compare = variant?.compare ?? product.compare;
        return {
          ...product, url: `${product.url}${variant ? `?variant=${variant.id}` : ''}`, image: product.image,
          price_min: price / 100, price_max: (variant ? price : product.price_max) / 100,
          price_varies: !variant && product.price !== product.price_max,
          compare_at_price_min: compare / 100,
        };
      });
      this.renderResults(q, results, 'fk-v1');
      this.open();
    } catch (err) {
      if (err.name !== 'AbortError' && !controller.signal.aborted && this.input.value.trim() === q) {
        this.panel.innerHTML = `<div class="fk-search__section"><a class="fk-search__all" data-result href="${this.form.action}?type=product&amp;q=${encodeURIComponent(q)}">${this.label('searchFor', 'Search for “[query]”', q)}</a></div>`;
        this.open();
      }
    } finally {
      if (this.controller === controller) this.removeAttribute('aria-busy');
    }
  }

  renderResults(q, r, engine = 'shopify') {
    this.renderedQuery = q;
    const products = r.products || [];
    const collections = r.collections || [];
    const articles = r.articles || [];
    const all = `${this.form.action}?type=product&amp;q=${encodeURIComponent(q)}`;
    let position = 0;
    const resultTracking = (type, index) => `data-fk-track="search_result_clicked" data-fk-track-surface="header" data-fk-track-result-type="${type}" data-fk-track-position="${index}" data-fk-track-search-term="${escapeHtml(q)}"`;
    let html = '';
    if (collections.length) html += `<div class="fk-search__section fk-search__section--links"><p class="fk-search__label">${this.label('categories', 'Categories')}</p><p class="fk-search__chips">${collections.map((c) => `<a href="${escapeHtml(c.url)}" data-result ${resultTracking('collection', ++position)}>${escapeHtml(c.title)}</a>`).join('')}</p></div>`;
    if (products.length) {
      html += `<div class="fk-search__section"><p class="fk-search__label">${engine === 'shopify-fallback' ? this.label('relatedProducts', 'Related products') : this.label('products', 'Products')}</p><ul class="fk-search__products" role="list">`;
      for (const p of products) {
        // Exact Shopify product mapped to SKU680554; see docs/product-fact-corrections.md.
        const imageQuarantined = String(p.id) === '16012832866634' || String(p.sku || '') === '680554';
        const img = imageQuarantined ? null : p.featured_image?.url || p.image;
        const src = img ? `${img}${img.includes('?') ? '&' : '?'}width=120` : '';
        const sale = !p.price_varies && Number(p.price_min) === Number(p.price_max ?? p.price_min) && p.compare_at_price_min && Number(p.compare_at_price_min) > Number(p.price_min);
        html += `<li><a class="fk-search__product" href="${escapeHtml(p.url)}" data-result ${resultTracking('product', ++position)}>
          <span class="fk-search__thumb">${src ? `<img src="${escapeHtml(src)}" alt="" loading="lazy" width="56" height="56">` : imageQuarantined ? `<span style="font-size:12px;line-height:1.2;text-align:center;overflow-wrap:anywhere">${this.label('imageUnavailable', 'Image unavailable')}</span>` : ''}</span>
          <span class="fk-search__text">${p.vendor ? `<span class="fk-search__vendor">${escapeHtml(p.vendor)}</span>` : ''}<span class="fk-search__title">${escapeHtml(p.title)}</span>
          <span class="fk-search__price${sale ? ' fk-search__price--sale' : ''}">${this.money(p.price_min)}${sale ? ` <span class="fk-price-saving">${(this.dataset.saveLabel || 'Save [amount]').replace('[amount]', this.money(Number(p.compare_at_price_min) - Number(p.price_min)))}</span>` : ''}</span></span></a></li>`;
      }
      html += `</ul></div>`;
    }
    if (articles.length) {
      html += `<div class="fk-search__section fk-search__section--links">`;
      if (articles.length) html += `<p class="fk-search__label">${this.label('recipes', 'Recipes')}</p><p class="fk-search__chips">${articles.map((a) => `<a href="${escapeHtml(a.url)}" data-result ${resultTracking('recipe', ++position)}>${escapeHtml(a.title)}</a>`).join('')}</p>`;
      html += `</div>`;
    }
    if (!html) html = `<div class="fk-search__section"><p class="fk-search__none">${this.label('noMatches', 'No matches for “[query]”.', q)}</p></div>`;
    html += `<div class="fk-search__section fk-search__section--footer"><a class="fk-search__all" href="${all}" data-result ${resultTracking('all_results', ++position)}>${this.label('searchFor', 'Search for “[query]”', q)} →</a></div>`;
    this.panel.innerHTML = html;
    this.active = -1;
    this.input.removeAttribute('aria-activedescendant');
    // Analytics (assets/fk-analytics.js) reports the term once typing settles; clicks carry type and position.
    document.dispatchEvent(new CustomEvent('fk:track', { detail: { event: 'search_suggestions_viewed', settle: 'search:header', properties: {
      surface: 'header', search_term: q, search_engine: engine, results_count: products.length + collections.length + articles.length, products_count: products.length,
    } } }));
  }

  // Text aus data-label-*, [query] wird escaped eingesetzt
  label(name, fallback, query) {
    const key = `label${name[0].toUpperCase()}${name.slice(1)}`;
    const text = escapeHtml(this.dataset[key] || fallback);
    return query === undefined ? text : text.replace('[query]', escapeHtml(query));
  }

  // Predictive Search liefert Preise in Hauptwährungseinheiten („15.32“)
  money(v) {
    const n = Number(v);
    if (!Number.isFinite(n)) return '';
    return formatMoney(Math.round(n * 100), this.format);
  }

  onKey(e) {
    const items = [...this.panel.querySelectorAll('[data-result]')];
    if (e.key === 'Escape') {
      clearTimeout(this.timer);
      this.controller?.abort();
      this.close();
      return;
    }
    if (this.panel.hidden || !items.length) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      this.active = e.key === 'ArrowDown' ? Math.min(items.length - 1, this.active + 1) : Math.max(-1, this.active - 1);
      items.forEach((el, i) => {
        el.classList.toggle('is-active', i === this.active);
        el.setAttribute('aria-selected', String(i === this.active));
      });
      if (this.active >= 0) this.input.setAttribute('aria-activedescendant', items[this.active].id);
      else this.input.removeAttribute('aria-activedescendant');
      items[this.active]?.scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'Enter' && this.active >= 0) {
      e.preventDefault();
      items[this.active].click();
    }
  }

  open() {
    this.panel.querySelectorAll('[data-result]').forEach((item, index) => {
      item.id = `${this.panel.id}-option-${index}`;
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', 'false');
    });
    this.panel.querySelectorAll('ul, li').forEach((item) => item.setAttribute('role', 'presentation'));
    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
    this.setAttribute('data-open', '');
  }

  close() {
    clearTimeout(this.timer);
    this.controller?.abort();
    this.removeAttribute('aria-busy');
    this.panel.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.panel.querySelectorAll('[data-result]').forEach((item) => {
      item.classList.remove('is-active');
      item.setAttribute('aria-selected', 'false');
    });
    this.removeAttribute('data-open');
    this.active = -1;
  }
}

// Shopify-Geldformate ({{amount}}, {{amount_with_comma_separator}} …) für Beträge in Cent
function formatMoney(cents, format) {
  const pattern = /\{\{\s*(\w+)\s*\}\}/;
  const type = (format.match(pattern) || [])[1] || 'amount';
  const group = (decimals, thousands, decimal) => {
    const [int, frac] = (Math.abs(cents) / 100).toFixed(decimals).split('.');
    return `${cents < 0 ? '-' : ''}${int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands)}${frac ? decimal + frac : ''}`;
  };
  const amounts = {
    amount: () => group(2, ',', '.'),
    amount_no_decimals: () => group(0, ',', '.'),
    amount_with_comma_separator: () => group(2, '.', ','),
    amount_no_decimals_with_comma_separator: () => group(0, '.', ','),
    amount_with_space_separator: () => group(2, ' ', ','),
    amount_no_decimals_with_space_separator: () => group(0, ' ', ''),
    amount_with_apostrophe_separator: () => group(2, "'", '.'),
    amount_with_period_and_space_separator: () => group(2, ' ', '.'),
  };
  return format.replace(pattern, (amounts[type] || amounts.amount)());
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

if (!customElements.get('fk-search')) customElements.define('fk-search', FkSearch);
